import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ArrowLeft, MapPin, DollarSign, Clock, Accessibility, Baby, Lightbulb, User, Lock, Droplets, SprayCanIcon, Loader2, Building2, Fuel, Mountain, Car, Utensils, HeartPulse, Train, Bus, TrainTrack } from "lucide-react";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "public_toilet", label: "Public Toilet", color: "#0ea5e9", Icon: User },
  { id: "mall", label: "Mall", color: "#a855f7", Icon: Building2 },
  { id: "fuel", label: "Petrol Pump", color: "#f97316", Icon: Fuel },
  { id: "toll_plaza", label: "Toll Plaza", color: "#dc2626", Icon: Car },
  { id: "highway_services", label: "Highway Services", color: "#16a34a", Icon: Mountain },
  { id: "restaurant", label: "Restaurant", color: "#ec4899", Icon: Utensils },
  { id: "hospital", label: "Hospital", color: "#ef4444", Icon: HeartPulse },
  { id: "metro_station", label: "Metro", color: "#7c3aed", Icon: Train },
  { id: "bus_station", label: "Bus Station", color: "#0891b2", Icon: Bus },
  { id: "railway_station", label: "Railway Station", color: "#ca8a04", Icon: TrainTrack },
];

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const ATTRIBUTES = [
  { id: "well_lit", label: "Well Lit", icon: Lightbulb },
  { id: "has_attendant", label: "Has Attendant", icon: User },
  { id: "wheelchair_accessible", label: "Wheelchair Accessible", icon: Accessibility },
  { id: "baby_changing", label: "Baby Changing Station", icon: Baby },
  { id: "separate_facilities", label: "Separate M/F Facilities", icon: User },
  { id: "working_locks", label: "Working Locks", icon: Lock },
  { id: "soap_available", label: "Soap/Sanitizer", icon: SprayCanIcon },
  { id: "running_water", label: "Running Water", icon: Droplets },
];

// Map click handler component
const MapClickHandler = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  return position ? <Marker position={position} /> : null;
};

export default function AddRestroomPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    type: "free",
    category: "public_toilet",
    price: "",
    is_24_hours: false,
    opening_time: "",
    closing_time: "",
    attributes: [],
  });

  // Reverse-geocode the dropped pin → auto-fill address
  useEffect(() => {
    if (!position) return;
    let cancelled = false;
    setReverseLoading(true);
    axios
      .get(`${API}/geocode/reverse`, { params: { lat: position.lat, lng: position.lng } })
      .then((r) => {
        if (cancelled) return;
        const display = r.data?.display_name;
        const a = r.data?.address || {};
        const niceName =
          a.amenity || a.shop || a.building || a.road || a.suburb || a.city || a.town;
        setFormData((prev) => ({
          ...prev,
          address: display || prev.address,
          name: prev.name || (niceName ? `${niceName}` : prev.name),
        }));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReverseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [position]);

  // Default center (Mumbai, India)
  const defaultCenter = [19.076, 72.8777];

  const handleAttributeToggle = (attrId) => {
    setFormData((prev) => ({
      ...prev,
      attributes: prev.attributes.includes(attrId)
        ? prev.attributes.filter((a) => a !== attrId)
        : [...prev.attributes, attrId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!position) {
      toast.error("Please select a location on the map");
      return;
    }

    if (!formData.name || !formData.address) {
      toast.error("Please fill in the name and address");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        latitude: position.lat,
        longitude: position.lng,
        price: formData.type === "paid" && formData.price ? parseFloat(formData.price) : null,
      };

      await axios.post(`${API}/restrooms`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Restroom submitted for review!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit restroom");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="add-restroom-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <h1 className="text-lg font-bold ml-4 flex items-center gap-2">
            <MapPin size={20} className="text-sky-600" />
            Add Restroom
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        {/* Map Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Select Location</CardTitle>
              <p className="text-sm text-slate-500">Click on the map to pin the restroom location</p>
            </CardHeader>
            <CardContent>
              <div className="h-64 rounded-xl overflow-hidden border border-slate-200">
                <MapContainer
                  center={defaultCenter}
                  zoom={13}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler position={position} setPosition={setPosition} />
                </MapContainer>
              </div>
              {position && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                  <MapPin size={14} />
                  Location selected: {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                  {reverseLoading && <Loader2 size={12} className="animate-spin ml-1" />}
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Basic Info Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Restroom Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name / Description *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Public Toilet near Central Station"
                    className="rounded-xl"
                    data-testid="restroom-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full address or landmark"
                    className="rounded-xl resize-none"
                    rows={2}
                    data-testid="restroom-address"
                  />
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <Label>Category</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="category-grid">
                    {CATEGORIES.map((c) => {
                      const Icon = c.Icon;
                      const active = formData.category === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, category: c.id })}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${
                            active ? "text-white border-transparent" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                          }`}
                          style={active ? { backgroundColor: c.color } : {}}
                          data-testid={`category-option-${c.id}`}
                        >
                          <Icon size={16} />
                          <span className="truncate">{c.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Type Selection */}
                <div className="space-y-3">
                  <Label>Type</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="free"
                        checked={formData.type === "free"}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="text-sky-600"
                      />
                      <span>Free</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value="paid"
                        checked={formData.type === "paid"}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="text-sky-600"
                      />
                      <span>Paid</span>
                    </label>
                  </div>
                </div>

                {formData.type === "paid" && (
                  <div className="space-y-2">
                    <Label htmlFor="price">Price (₹)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <Input
                        id="price"
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="10"
                        className="pl-10 rounded-xl"
                      />
                    </div>
                  </div>
                )}

                {/* Operating Hours */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="is_24_hours" className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-500" />
                      Open 24 Hours
                    </Label>
                    <Switch
                      id="is_24_hours"
                      checked={formData.is_24_hours}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_24_hours: checked })}
                    />
                  </div>

                  {!formData.is_24_hours && (
                    <div className="flex gap-4">
                      <div className="flex-1 space-y-1">
                        <Label className="text-sm">Opening Time</Label>
                        <Input
                          type="time"
                          value={formData.opening_time}
                          onChange={(e) => setFormData({ ...formData, opening_time: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-sm">Closing Time</Label>
                        <Input
                          type="time"
                          value={formData.closing_time}
                          onChange={(e) => setFormData({ ...formData, closing_time: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Attributes */}
                <div className="space-y-3">
                  <Label>Facilities & Features</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {ATTRIBUTES.map((attr) => {
                      const Icon = attr.icon;
                      return (
                        <label
                          key={attr.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                            formData.attributes.includes(attr.id)
                              ? "border-sky-500 bg-sky-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <Checkbox
                            checked={formData.attributes.includes(attr.id)}
                            onCheckedChange={() => handleAttributeToggle(attr.id)}
                          />
                          <Icon size={16} className="text-slate-500" />
                          <span className="text-sm">{attr.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold mt-4"
                  data-testid="submit-restroom"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    "Submit for Review"
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Your submission will be reviewed before being published.
                </p>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
